import { ansiHeaderDataSchema, barcodeAamvaDataSchema } from '../types/BarcodeAamvaData';
/**
 * Control Sequence required by the AAMVA DL/ID Card Design Standard to mark the start of the PDF417 barcode data
 */
export const aamvaDataElementSeparator = '\n';
const aamvaRecordSeparator = '\x1E';
const aamvaSegmentTerminator = '\r';
const aamvaStartControlSequence = `@${aamvaDataElementSeparator}${aamvaRecordSeparator}${aamvaSegmentTerminator}`;
const subFileDesignator = 'DL';
/**
 * Map {@link BarcodeAamvaData} object to final AAMVA String Representation
 *
 * @param data - {@link BarcodeAamvaData} object
 * @param config {@link BarcodeAamvaDataConfiguration} customize AAMVA fields
 */
export function mapBarcodeAamvaDataToAamvaString(data, config) {
    const aamvaData = barcodeAamvaDataSchema.parse(data);
    // 'DAJ', aamvaData.dajState, config?.dajState
    const aamvaElements = [
        toAamvaElement('DAJ', aamvaData.dajState, config === null || config === void 0 ? void 0 : config.dajState),
        toAamvaElement('DAK', aamvaData.dakZipCode, config === null || config === void 0 ? void 0 : config.dakZipCode),
        `DAQ${aamvaData.daqBadgeNumber}`,
        toAamvaElement('DAY', aamvaData.dayEyeColor, config === null || config === void 0 ? void 0 : config.dayEyeColor),
        toAamvaElement(`DBA`, aamvaData.dbaExpirationDate, config === null || config === void 0 ? void 0 : config.dbaExpirationDate),
        toAamvaElement('DBB', aamvaData.dbbDateOfBirth, config === null || config === void 0 ? void 0 : config.dbbDateOfBirth),
        toAamvaElement('DBC', aamvaData.dbcGender, config === null || config === void 0 ? void 0 : config.dbcGender),
        toAamvaElement(`DBD`, aamvaData.dbdIssueDate, config === null || config === void 0 ? void 0 : config.dbdIssueDate),
        toAamvaElement(`DCF`, aamvaData.dcfRevisionNumber, config === null || config === void 0 ? void 0 : config.dcfRevisionNumber),
        toAamvaElement(`DCG`, aamvaData.dcgCountry, config === null || config === void 0 ? void 0 : config.dcgCountry),
        toAamvaElement(`DCS`, aamvaData.dcsLastName, config === null || config === void 0 ? void 0 : config.dcsLastName),
        toAamvaElement(`DCT`, aamvaData.dctFirstAndMiddleName, config === null || config === void 0 ? void 0 : config.dctFirstAndMiddleName),
        toAamvaElement(`ZCE`, aamvaData.zceAffiliation, config === null || config === void 0 ? void 0 : config.zceAffiliation),
        toAamvaElement('ZCT', aamvaData.zctOccupationTitle, config === null || config === void 0 ? void 0 : config.zctOccupationTitle),
        toAamvaElement('ZCL', aamvaData.zclAccessLevel, config === null || config === void 0 ? void 0 : config.zclAccessLevel),
        toAamvaElement('DAU', aamvaData.dauHeight, config === null || config === void 0 ? void 0 : config.dauHeight),
        toAamvaElement('DAI', aamvaData.daiCity, config === null || config === void 0 ? void 0 : config.daiCity),
    ].filter((el) => Boolean(el));
    const { ansiHeader, aamvaString } = mapBarcodeAamvaElementsArrayToAamvaString(aamvaElements, aamvaData.ansiHeaderData);
    return {
        ansiHeader,
        aamvaElements,
        aamvaString,
    };
}
function toAamvaElement(aamvaKey, value, configFieldValue) {
    if (!value) {
        return undefined;
    }
    if (configFieldValue && !configFieldValue.enabled) {
        return undefined;
    }
    return `${aamvaKey}${value}`;
}
/**
 * Map AAMVA String Array to final AAMVA String Representation
 *
 * @param aamvaDataElements - AAMVA String Array generated with {@link mapBarcodeAamvaDataToAamvaString}
 * @param ansiHeaderData
 */
export function mapBarcodeAamvaElementsArrayToAamvaString(aamvaDataElements, ansiHeaderData) {
    const headerData = ansiHeaderDataSchema.parse(ansiHeaderData);
    const aamvaElements = aamvaDataElements.filter((el) => Boolean(el));
    const aamvaElementsStr = mapAamvaElementsToFinalString(aamvaElements);
    /*
        ANSI Header
        e.g.: ANSI 000001030001DL00310220 -> ANSI 000001030001.DL.0031.0220
    */
    let ansiHeader = headerData.badgeIssuerId +
        headerData.aamvaVersionMajor +
        headerData.aamvaVersionMinor +
        headerData.aamvaSubfilesCount +
        subFileDesignator +
        `????` + // offset first character after the header, less error-prone than math in-place
        aamvaElementsStr.length.toString().padStart(4, '0'); // length of elements
    let aamvaFullHeader = `${aamvaStartControlSequence}ANSI ${ansiHeader}`;
    aamvaFullHeader = aamvaFullHeader.replace('????', aamvaFullHeader.length.toString().padStart(4, '0'));
    ansiHeader = ansiHeader.replace('????', aamvaFullHeader.length.toString().padStart(4, '0'));
    return {
        ansiHeader,
        aamvaString: aamvaFullHeader +
            subFileDesignator + // according to spec example ("ANSI 636000100002DL00410278ZV03190008DLDAQT64235789") and RMS Classic
            aamvaElementsStr,
    };
}
function mapAamvaElementsToFinalString(aamvaElements) {
    if (aamvaElements.some((el) => el.startsWith('ANSI'))) {
        throw new Error('Elements should only include content, not ANSI header');
    }
    return aamvaElements.map((el) => `${el}${aamvaDataElementSeparator}`).join('');
}
